import React from "react";
import { Button, Card, Typography, List } from "antd";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

export interface AuthRequiredInfoBannerProps {
  /**
   * Title of the feature/page
   */
  title: string;
  /**
   * Description of what the feature does
   */
  description: string;
  /**
   * List of benefits/use cases for the feature
   */
  benefits: string[];
  /**
   * Custom CTA button text (default: "Sign In" or "Get Started")
   */
  ctaText?: string;
  /**
   * URL to redirect to after authentication (default: current location)
   */
  returnUrl?: string;
}

/**
 * AuthRequiredInfoBanner Component
 *
 * Displays an informational banner when authentication is required to access a feature.
 * Shows the feature title, description, benefits, and a call-to-action button that
 * links to the signup page with a return URL parameter.
 *
 * @param props - Component props
 * @returns JSX element
 */
export const AuthRequiredInfoBanner: React.FC<AuthRequiredInfoBannerProps> = ({
  title,
  description,
  benefits,
  ctaText,
  returnUrl,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  // Use provided returnUrl or default to current pathname
  const finalReturnUrl = returnUrl || location.pathname + location.search;

  // Format signup link with returnUrl parameter
  const signupLink = `/signup?returnUrl=${encodeURIComponent(finalReturnUrl)}`;

  // Default CTA text if not provided - use "Sign up" as default
  const defaultCtaText = ctaText || t("Sign_up");

  return (
    <Card
      style={{
        marginTop: "2rem",
        textAlign: "center",
      }}
      bodyStyle={{
        padding: "3rem 2rem",
      }}
    >
      <Title level={2} style={{ marginBottom: "1rem" }}>
        {title}
      </Title>

      <Paragraph
        style={{
          fontSize: "1.1rem",
          marginBottom: "2rem",
          color: "rgba(0, 0, 0, 0.65)",
        }}
      >
        {description}
      </Paragraph>

      {benefits.length > 0 && (
        <List
          dataSource={benefits}
          renderItem={(benefit) => (
            <List.Item
              style={{
                border: "none",
                padding: "0.5rem 0",
                justifyContent: "center",
              }}
            >
              <CheckCircleOutlined
                style={{
                  color: "#52c41a",
                  marginRight: "0.75rem",
                  fontSize: "1rem",
                }}
              />
              <span style={{ fontSize: "1rem" }}>{benefit}</span>
            </List.Item>
          )}
          style={{
            marginBottom: "2rem",
            textAlign: "left",
            display: "inline-block",
          }}
        />
      )}

      <Link to={signupLink}>
        <Button type="primary" size="large">
          {defaultCtaText}
        </Button>
      </Link>
    </Card>
  );
};
